import React from "react";
import { format, isToday, isYesterday, isThisYear } from "date-fns";

interface MessageTimestampProps {
  timestamp: Date;
}

/**
 * Renders a contextual timestamp for a chat message.
 * Shows time-only for today, a "Yesterday" prefix for the prior day,
 * month/day for earlier this year, and a full date for older messages.
 * The precise localized date/time is always available on hover, and the
 * value is exposed via a semantic <time> element. Renders nothing for an
 * invalid date.
 * @param timestamp - The message creation date/time to display
 * @returns A time element containing the formatted timestamp, or null when invalid
 */
export const MessageTimestamp: React.FC<MessageTimestampProps> = ({
  timestamp,
}) => {
  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }

  return (
    <time
      dateTime={timestamp.toISOString()}
      title={format(timestamp, "PPpp")}
      className="text-[11px] leading-none tracking-tight text-muted-foreground/80 tabular-nums whitespace-nowrap"
    >
      {formatChatTimestamp(timestamp)}
    </time>
  );
};

/**
 * Formats a date relative to the current day for compact chat display.
 * @param date - The date to format
 * @returns A human-friendly timestamp string
 */
function formatChatTimestamp(date: Date): string {
  if (isToday(date)) {
    return format(date, "h:mm a");
  }
  if (isYesterday(date)) {
    return `Yesterday ${format(date, "h:mm a")}`;
  }
  if (isThisYear(date)) {
    return format(date, "MMM d, h:mm a");
  }
  return format(date, "MMM d, yyyy");
}
